import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  InputBase,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { MARKETPLACE_KEYWORD_MAX_LENGTH } from '../../utils/marketplace/marketplaceKeywordSearch';
import useDashboardOmnibarSearch from '../../hooks/shared/useDashboardOmnibarSearch';
import DashboardOmnibarSuggestions from './DashboardOmnibarSuggestions';
import { FONT_SIZES } from '../../Theme';

const COLLAPSED_WIDTH_PX = 28;
const EXPANDED_MAX_WIDTH_PX = 640;
const EXPANDED_MIN_WIDTH_PX = 360;
const PANEL_SLIDE_MS = 280;
const FIELD_SLIDE_MS = 280;
const SLIDE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
}

function slideTransition(theme, properties) {
  return theme.transitions.create(properties, {
    duration: FIELD_SLIDE_MS,
    easing: SLIDE_EASE,
  });
}

function clearScheduledSlide(timerRef, frameRef) {
  window.clearTimeout(timerRef.current);
  window.cancelAnimationFrame(frameRef.current);
}

/**
 * Stripe-style dashboard omnibar for the Navbar.
 * Empty focus: Filter by categories. Typed: pages, entities, marketplace fallback.
 * Power users can still type prefix:value operators.
 */
function DashboardOmnibarSearch({
  userType = null,
  placeholder = 'Search',
  inputId = 'dashboard-omnibar-search',
}) {
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const suppressSubmitRef = useRef(false);
  const panelSnapshotRef = useRef({ items: [], mode: 'empty', categoryMode: null });
  const openRef = useRef(false);
  const fieldStaysRef = useRef(false);
  const closeTimerRef = useRef(0);
  const frameRef = useRef(0);
  const slideTokenRef = useRef(0);
  const closePanelRef = useRef(() => {});
  const [open, setOpen] = useState(false);
  const [holdFieldOpen, setHoldFieldOpen] = useState(false);
  const [fieldClosing, setFieldClosing] = useState(false);
  const [closingWidth, setClosingWidth] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const {
    draft,
    setDraftFromInput,
    inputDisplayValue,
    categoryMode,
    activeFilterChipLabel,
    promptPlaceholder,
    ensureIndex,
    getSuggestions,
    selectSuggestion,
    submit,
    clear,
    clearActiveFilter,
    resetCategoryMode,
  } = useDashboardOmnibarSearch(userType);

  const listId = `${inputId}-suggestions`;
  const optionIdPrefix = `${inputId}-option`;
  const trimmed = String(draft || '').trim();
  const suggestionItems = getSuggestions(draft, open);
  const showSuggestions = open && suggestionItems.length > 0;
  let suggestionMode = 'typed';
  if (categoryMode) {
    suggestionMode = 'drilldown';
  } else if (trimmed.length === 0) {
    suggestionMode = 'empty';
  }
  if (open) {
    panelSnapshotRef.current = {
      items: suggestionItems,
      mode: suggestionMode,
      categoryMode,
    };
  }
  const panelItems = open ? suggestionItems : panelSnapshotRef.current.items;
  const panelMode = open ? suggestionMode : panelSnapshotRef.current.mode;
  const panelCategoryMode = open ? categoryMode : panelSnapshotRef.current.categoryMode;
  const showPanel = showSuggestions || (holdFieldOpen && panelItems.length > 0);
  openRef.current = open;
  fieldStaysRef.current = Boolean(trimmed || activeFilterChipLabel);
  const resolvedPlaceholder = promptPlaceholder || placeholder;
  const contentExpanded = open
    || holdFieldOpen
    || Boolean(trimmed)
    || Boolean(categoryMode)
    || Boolean(activeFilterChipLabel);
  const widthExpanded = contentExpanded && !fieldClosing;
  const showExpandedChrome = widthExpanded || fieldClosing;
  const activeOptionId = activeIndex >= 0 && activeIndex < suggestionItems.length
    ? `${optionIdPrefix}-${activeIndex}`
    : undefined;
  let rootWidth = COLLAPSED_WIDTH_PX;
  let rootMaxWidth = COLLAPSED_WIDTH_PX;
  if (closingWidth != null) {
    rootWidth = closingWidth;
    rootMaxWidth = closingWidth;
  } else if (widthExpanded) {
    rootWidth = '100%';
    rootMaxWidth = EXPANDED_MAX_WIDTH_PX;
  }
  const pillMinWidth = widthExpanded ? { xs: 0, sm: EXPANDED_MIN_WIDTH_PX } : 0;

  useEffect(() => {
    setActiveIndex(-1);
  }, [trimmed, suggestionMode, open, categoryMode]);

  useEffect(() => () => {
    slideTokenRef.current += 1;
    clearScheduledSlide(closeTimerRef, frameRef);
  }, []);

  const cancelFieldSlide = () => {
    slideTokenRef.current += 1;
    clearScheduledSlide(closeTimerRef, frameRef);
  };

  const beginFieldCollapse = () => {
    cancelFieldSlide();
    if (openRef.current || fieldStaysRef.current || prefersReducedMotion()) {
      setFieldClosing(false);
      setClosingWidth(null);
      return;
    }
    const measured = Math.round(
      rootRef.current?.getBoundingClientRect().width || EXPANDED_MIN_WIDTH_PX,
    );
    const token = slideTokenRef.current;
    setFieldClosing(true);
    setClosingWidth(measured);
    const outerFrame = window.requestAnimationFrame(() => {
      const innerFrame = window.requestAnimationFrame(() => {
        if (slideTokenRef.current !== token) return;
        setClosingWidth(COLLAPSED_WIDTH_PX);
        closeTimerRef.current = window.setTimeout(() => {
          if (slideTokenRef.current !== token) return;
          setFieldClosing(false);
          setClosingWidth(null);
        }, FIELD_SLIDE_MS);
      });
      frameRef.current = innerFrame;
    });
    frameRef.current = outerFrame;
  };

  const openPanel = () => {
    cancelFieldSlide();
    setFieldClosing(false);
    setClosingWidth(null);
    setHoldFieldOpen(false);
    setOpen(true);
    setActiveIndex(-1);
    ensureIndex();
  };

  const finishClose = () => {
    setHoldFieldOpen(false);
    resetCategoryMode();
    beginFieldCollapse();
  };

  const closePanel = () => {
    const slidePanel = openRef.current
      && panelSnapshotRef.current.items.length > 0
      && !prefersReducedMotion();
    setOpen(false);
    setActiveIndex(-1);
    if (slidePanel) {
      setHoldFieldOpen(true);
      return;
    }
    finishClose();
  };
  closePanelRef.current = closePanel;

  const handlePanelExited = () => {
    if (openRef.current) return;
    finishClose();
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closePanelRef.current();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  const hasSearchQuery = Boolean(trimmed || categoryMode || activeFilterChipLabel);

  const commitTypedSubmit = () => {
    const result = submit();
    if (result && result.navigated === false && result.reason !== 'insert') {
      openPanel();
      return;
    }
    if (result?.navigated) {
      closePanel();
    }
  };

  const handleExpandClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Ignore the stray submit that can fire when this button is replaced by the
    // in-field search button in the same click.
    suppressSubmitRef.current = true;
    openPanel();
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const runSearchSubmit = () => {
    if (suppressSubmitRef.current && !hasSearchQuery) {
      suppressSubmitRef.current = false;
      return;
    }
    suppressSubmitRef.current = false;
    if (!hasSearchQuery) {
      openPanel();
      window.requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    commitTypedSubmit();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearchSubmit();
  };

  const handleSearchIconClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    runSearchSubmit();
  };

  const handleClear = () => {
    clear();
    openPanel();
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleClearActiveFilter = () => {
    clearActiveFilter();
    openPanel();
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSelectSuggestion = (item) => {
    const result = selectSuggestion(item);
    if (result?.reason === 'insert' || result?.reason === 'drilldown') {
      openPanel();
      window.requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    if (result?.navigated) {
      closePanel();
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (categoryMode) {
        resetCategoryMode();
        setActiveIndex(-1);
        return;
      }
      closePanel();
      return;
    }

    if (event.key === 'Backspace'
      && !inputDisplayValue
      && activeFilterChipLabel
      && !event.metaKey
      && !event.ctrlKey
      && !event.altKey) {
      event.preventDefault();
      handleClearActiveFilter();
      return;
    }

    if (event.key === 'Tab') {
      closePanel();
      return;
    }

    if (event.key === 'Enter') {
      if (activeIndex >= 0 && suggestionItems[activeIndex]) {
        event.preventDefault();
        handleSelectSuggestion(suggestionItems[activeIndex]);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openPanel();
        return;
      }
      if (!suggestionItems.length) return;
      setActiveIndex((prev) => {
        if (prev < 0) return 0;
        return (prev + 1) % suggestionItems.length;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openPanel();
        return;
      }
      if (!suggestionItems.length) return;
      setActiveIndex((prev) => {
        if (prev < 0) return suggestionItems.length - 1;
        return (prev - 1 + suggestionItems.length) % suggestionItems.length;
      });
    }
  };

  return (
    <Box
      ref={rootRef}
      component="form"
      role="search"
      onSubmit={handleSubmit}
      data-testid="dashboard-omnibar-search"
      data-expanded={widthExpanded ? 'true' : 'false'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: rootWidth,
        maxWidth: rootMaxWidth,
        minWidth: 0,
        flexShrink: widthExpanded ? 1 : 0,
        height: 'auto',
        margin: 0,
        position: 'relative',
        transition: (theme) => slideTransition(theme, ['max-width', 'width']),
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
    >
      <Box
        data-testid="dashboard-omnibar-pill"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: widthExpanded ? 0.5 : 0,
          height: 32,
          width: '100%',
          minWidth: pillMinWidth,
          pl: widthExpanded ? 2 : 0,
          pr: widthExpanded ? 0.5 : 0,
          borderRadius: 999,
          bgcolor: widthExpanded ? 'background.soft' : 'transparent',
          boxShadow: 'none',
          overflow: 'hidden',
          transition: (theme) => slideTransition(theme, ['min-width', 'padding', 'background-color', 'gap']),
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
        }}
      >
        {!showExpandedChrome ? (
          <IconButton
            type="button"
            onClick={handleExpandClick}
            aria-label="Open search"
            aria-expanded={false}
            aria-controls={inputId}
            sx={{
              flexShrink: 0,
              width: COLLAPSED_WIDTH_PX,
              height: COLLAPSED_WIDTH_PX,
              bgcolor: 'favorite.main',
              color: 'common.white',
              '&:hover': {
                bgcolor: 'favorite.dark',
              },
              '&:active': {
                bgcolor: 'favorite.dark',
              },
              '&.Mui-focusVisible': {
                outline: (theme) => `2px solid ${theme.palette.favorite.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <SearchIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : null}
        {showExpandedChrome && activeFilterChipLabel ? (
          <Box
            component="span"
            data-testid="dashboard-omnibar-active-filter"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              flexShrink: 0,
              maxWidth: '42%',
              pl: 1,
              pr: 0.25,
              py: 0.25,
              borderRadius: 999,
              bgcolor: 'background.soft',
              color: 'text.primary',
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: FONT_SIZES.caption,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                pl: 0.25,
              }}
            >
              {activeFilterChipLabel}
            </Typography>
            <IconButton
              type="button"
              size="small"
              onClick={handleClearActiveFilter}
              aria-label="Clear filter"
              sx={{
                width: 22,
                height: 22,
                color: 'text.secondary',
              }}
            >
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ) : null}
        {showExpandedChrome ? (
          <InputBase
            inputRef={inputRef}
            id={inputId}
            value={inputDisplayValue}
            onChange={(event) => {
              setDraftFromInput(event.target.value);
              openPanel();
            }}
            onFocus={() => {
              openPanel();
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={resolvedPlaceholder}
            type="search"
            inputProps={{
              role: 'combobox',
              autoComplete: 'off',
              inputMode: 'search',
              maxLength: MARKETPLACE_KEYWORD_MAX_LENGTH,
              'aria-label': resolvedPlaceholder,
              'aria-autocomplete': 'list',
              'aria-controls': showSuggestions ? listId : undefined,
              'aria-expanded': showSuggestions,
              'aria-activedescendant': activeOptionId,
            }}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              fontSize: FONT_SIZES.body,
              '& input': {
                py: 0.5,
                pr: 0.5,
                border: 'none',
                outline: 'none',
                '&::-webkit-search-cancel-button': {
                  display: 'none',
                },
                '&::placeholder': {
                  opacity: 1,
                  color: 'text.secondary',
                },
              },
            }}
          />
        ) : null}
        {showExpandedChrome && (draft || categoryMode) ? (
          <IconButton
            type="button"
            size="small"
            onClick={handleClear}
            aria-label="Clear search"
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              color: 'text.secondary',
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
        {showExpandedChrome ? (
          <IconButton
            type="button"
            onClick={handleSearchIconClick}
            aria-label="Search"
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              bgcolor: 'favorite.main',
              color: 'common.white',
              '&:hover': {
                bgcolor: 'favorite.dark',
              },
              '&:active': {
                bgcolor: 'favorite.dark',
              },
              '&.Mui-focusVisible': {
                outline: (theme) => `2px solid ${theme.palette.favorite.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <SearchIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : null}
      </Box>
      {showPanel ? (
        <Collapse
          in={showSuggestions}
          timeout={prefersReducedMotion() ? 0 : PANEL_SLIDE_MS}
          easing={SLIDE_EASE}
          unmountOnExit
          onExited={handlePanelExited}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            zIndex: 10020,
            pointerEvents: showSuggestions ? 'auto' : 'none',
          }}
        >
          <DashboardOmnibarSuggestions
            mode={panelMode}
            items={panelItems}
            onSelect={handleSelectSuggestion}
            listId={listId}
            activeIndex={activeIndex}
            optionIdPrefix={optionIdPrefix}
            categoryMode={panelCategoryMode}
          />
        </Collapse>
      ) : null}
    </Box>
  );
}

export default DashboardOmnibarSearch;
